import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, MapPin, Users, Calendar, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useIsMobile } from '@/hooks/use-mobile';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';

interface GameData {
  id: string;
  game_title: string;
  game_description: string;
  game_date: string;
  game_time: string;
  field_location: string;
  game_code: string;
  game_status: string;
  home_team: {
    team_id: number;
    team_name: string;
    team_logo: string;
  } | null;
  away_team: {
    team_id: number;
    team_name: string;
    team_logo: string;
  } | null;
}

interface GameParticipant {
  id: string;
  player_id: string;
  team_side: string;
  position?: string;
  participation_status: string;
  eligibility_status: string;
  player_type: string;
  player_name?: string;
}

export default function GameDetails() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [game, setGame] = useState<GameData | null>(null);
  const [roster, setRoster] = useState<GameParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!gameId) return;
    
    // Update page title
    document.title = `games/${game?.game_code || gameId.slice(0, 6)}`;
  }, [gameId, game?.game_code]);

  useEffect(() => {
    if (!gameId) return;
    
    const fetchGameDetails = async () => {
      try {
        // Try to fetch from venue_games first
        let gameData = null;
        let gameError = null;
        
        const { data: venueGameData, error: venueError } = await supabase
          .from('venue_games')
          .select(`
            *,
            home_team:teams!venue_games_home_team_id_fkey(team_id, team_name, team_logo),
            away_team:teams!venue_games_away_team_id_fkey(team_id, team_name, team_logo)
          `)
          .eq('id', gameId)
          .single();

        if (venueGameData && !venueError) {
          gameData = {
            ...venueGameData,
            game_title: venueGameData.game_title || `${venueGameData.home_team?.team_name || 'Home'} vs ${venueGameData.away_team?.team_name || 'Away'}`,
            field_location: venueGameData.field_location || 'TBD'
          };
        } else {
          // If not found in venue_games, try the games table
          const { data: regularGameData, error: regularError } = await supabase
            .from('games')
            .select(`
              *,
              home_team:teams!games_home_team_id_fkey(team_id, team_name, team_logo),
              away_team:teams!games_away_team_id_fkey(team_id, team_name, team_logo)
            `)
            .eq('id', gameId)
            .single();

          if (regularGameData && !regularError) {
            gameData = {
              ...regularGameData,
              game_title: regularGameData.game_name,
              field_location: regularGameData.venue_address || regularGameData.venue_name || 'TBD',
              game_description: `${regularGameData.home_team_label} vs ${regularGameData.away_team_label}`
            };
          } else {
            gameError = regularError || venueError;
          }
        }

        if (!gameData || gameError) throw gameError;
        
        setGame(gameData);

        // Fetch roster data from game_participants
        try {
          const { data: participantsData, error: participantsError } = await supabase
            .from('game_participants')
            .select(`
              id,
              player_id,
              team_side,
              position,
              participation_status,
              eligibility_status,
              player_type
            `)
            .eq('game_id', gameId);

          if (participantsError) throw participantsError;

          // Get player names for both supabase and internal users
          const rosterWithNames = await Promise.all(
            (participantsData || []).map(async (participant) => {
              let playerName = 'Unknown Player';
              
              try {
                if (participant.player_type === 'supabase') {
                  const { data: profileData, error: profileError } = await supabase
                    .from('profiles')
                    .select('username, first_name, last_name')
                    .eq('id', participant.player_id)
                    .maybeSingle();
                  
                  if (profileData && !profileError) {
                    playerName = profileData.username || 
                                 `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() ||
                                 'Supabase User';
                  }
                } else if (participant.player_type === 'internal') {
                  const { data: internalData, error: internalError } = await supabase
                    .from('internal_users')
                    .select('username')
                    .eq('id', participant.player_id)
                    .maybeSingle();
                  
                  if (internalData && !internalError) {
                    playerName = internalData.username || 'Internal User';
                  }
                }
              } catch (error) {
                console.warn(`Failed to fetch player name for ${participant.player_id}:`, error);
              }

              return {
                ...participant,
                player_name: playerName
              };
            })
          );

          setRoster(rosterWithNames);
        } catch (rosterError) {
          console.warn('Could not fetch roster data:', rosterError);
          setRoster([]);
        }

      } catch (err) {
        console.error('Error fetching game details:', err);
        setError('Failed to load game details');
      } finally {
        setLoading(false);
      }
    };

    fetchGameDetails();
  }, [gameId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <span className="ml-3 text-lg">Loading game details...</span>
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <h3 className="text-lg font-semibold text-destructive mb-2">Error Loading Game</h3>
            <p className="text-muted-foreground">{error || 'Game not found'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const homeRoster = roster.filter(player => player.team_side === 'home');
  const awayRoster = roster.filter(player => player.team_side === 'away');

  return (
    <div className="flex flex-col min-h-screen bg-black w-full">
      {/* Desktop Header */}
      {!isMobile && <Header />}
      {/* Mobile Header */}
      {isMobile && <MobileHeader />}
      
      <main className={`flex-grow w-full ${
        isMobile 
          ? 'pt-16 mb-16 py-4 px-4' 
          : 'mt-24 mb-6 py-6 md:py-12 px-4 md:px-6'
      }`}>
        <div className="container mx-auto max-w-4xl">
          {/* Back Button */}
          <div className="mb-6">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/game-management')}
              className="flex items-center space-x-2 text-white hover:text-black hover:bg-white"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Game Management</span>
            </Button>
          </div>

      {/* Game Header */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl">
              Home Team: {game.home_team?.team_name || 'TBD'} vs Away Team: {game.away_team?.team_name || 'TBD'}
            </CardTitle>
            <Badge variant={game.game_status === 'confirmed' ? 'default' : 'secondary'}>
              {game.game_status.toUpperCase()}
            </Badge>
          </div>
          <p className="text-muted-foreground">{game.game_description}</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>{format(new Date(game.game_date), 'MMM dd, yyyy')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>{game.game_time}</span>
            </div>
            <div className="flex items-center space-x-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span>{game.field_location}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="font-mono text-sm bg-muted px-2 py-1 rounded">
                {game.game_code}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Teams */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <span>Home Team</span>
              {game.home_team && (
                <Badge variant="outline">{game.home_team.team_name}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {game.home_team ? (
              <div className="flex items-center space-x-3">
                {game.home_team.team_logo && (
                  <img 
                    src={game.home_team.team_logo} 
                    alt={game.home_team.team_name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                )}
                <div>
                  <h3 className="font-semibold">{game.home_team.team_name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {homeRoster.length} players registered
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">No home team assigned</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <span>Away Team</span>
              {game.away_team && (
                <Badge variant="outline">{game.away_team.team_name}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {game.away_team ? (
              <div className="flex items-center space-x-3">
                {game.away_team.team_logo && (
                  <img 
                    src={game.away_team.team_logo} 
                    alt={game.away_team.team_name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                )}
                <div>
                  <h3 className="font-semibold">{game.away_team.team_name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {awayRoster.length} players registered
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">No away team assigned</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Roster Information */}
      {roster.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Users className="h-5 w-5" />
              <span>Game Roster</span>
              <Badge variant="outline">{roster.length} total players</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {game.home_team && homeRoster.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">
                    {game.home_team.team_name} Players ({homeRoster.length})
                  </h4>
                     <div className="space-y-3">
                      {homeRoster.map((player) => {
                        const isPresent = ['confirmed', 'ready_to_play'].includes(player.participation_status);
                        const isEligible = player.eligibility_status === 'eligible';
                        
                        return (
                          <div key={player.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                            <div className="flex items-center space-x-3">
                              {isPresent ? (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-500" />
                              )}
                              <div>
                                <div className="font-medium text-sm">
                                  {player.player_name || `Player ${player.player_id.slice(0, 8)}`}
                                </div>
                                {player.position && (
                                  <Badge variant="outline" className="text-xs mt-1">
                                    {player.position}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Badge variant={isPresent ? "default" : "secondary"} className="text-xs">
                                {player.participation_status.replace('_', ' ').toUpperCase()}
                              </Badge>
                              <Badge variant={isEligible ? "default" : "destructive"} className="text-xs">
                                {player.eligibility_status.toUpperCase()}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                </div>
              )}

              {game.away_team && awayRoster.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">
                    {game.away_team.team_name} Players ({awayRoster.length})
                  </h4>
                   <div className="space-y-3">
                      {awayRoster.map((player) => {
                        const isPresent = ['confirmed', 'ready_to_play'].includes(player.participation_status);
                        const isEligible = player.eligibility_status === 'eligible';
                        
                        return (
                          <div key={player.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                            <div className="flex items-center space-x-3">
                              {isPresent ? (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-500" />
                              )}
                              <div>
                                <div className="font-medium text-sm">
                                  {player.player_name || `Player ${player.player_id.slice(0, 8)}`}
                                </div>
                                {player.position && (
                                  <Badge variant="outline" className="text-xs mt-1">
                                    {player.position}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Badge variant={isPresent ? "default" : "secondary"} className="text-xs">
                                {player.participation_status.replace('_', ' ').toUpperCase()}
                              </Badge>
                              <Badge variant={isEligible ? "default" : "destructive"} className="text-xs">
                                {player.eligibility_status.toUpperCase()}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {roster.length === 0 && (
        <Card>
          <CardContent className="p-6 text-center">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <h3 className="text-lg font-semibold mb-2">No Roster Data</h3>
            <p className="text-muted-foreground">
              No players have been registered for this game yet.
            </p>
          </CardContent>
        </Card>
      )}
        </div>
      </main>
      
      {/* Desktop Footer */}
      {!isMobile && <Footer />}
      {/* Mobile Bottom Navigation */}
      {isMobile && <MobileBottomNav />}
    </div>
  );
}